import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Plus, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const TeamManagement = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTeamDialogOpen, setIsTeamDialogOpen] = useState(false);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);

  const [teamForm, setTeamForm] = useState({ name: '', description: '' });
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'reviewer',
    team_id: '',
  });

  useEffect(() => {
    fetchTeams();
    fetchAllUsers();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/teams`, teamForm);
      toast.success('Team created successfully!');
      setIsTeamDialogOpen(false);
      setTeamForm({ name: '', description: '' });
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, userForm);
      toast.success('User created successfully!');
      setIsUserDialogOpen(false);
      setUserForm({ email: '', password: '', name: '', role: 'reviewer', team_id: '' });
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const getUsersInTeam = (teamId) => {
    return allUsers.filter((u) => u.team_id === teamId);
  };

  const getUnassignedUsers = () => {
    return allUsers.filter((u) => !u.team_id);
  };

  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="team-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Team Management
            </h1>
            <p className="text-slate-600">Organize users into teams for better collaboration</p>
          </div>
          <div className="flex gap-3">
            <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-slate-300" data-testid="add-user-button">
                  <UserPlus size={20} className="mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New User</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateUser} className="space-y-4" data-testid="user-form">
                  <div>
                    <Label htmlFor="user-name">Full Name</Label>
                    <Input
                      id="user-name"
                      placeholder="John Doe"
                      value={userForm.name}
                      onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                      required
                      data-testid="user-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="user-email">Email</Label>
                    <Input
                      id="user-email"
                      type="email"
                      placeholder="john@example.com"
                      value={userForm.email}
                      onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                      required
                      data-testid="user-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="user-password">Password</Label>
                    <Input
                      id="user-password"
                      type="password"
                      placeholder="••••••••"
                      value={userForm.password}
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      required
                      data-testid="user-password-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="user-role">Role</Label>
                    <Select value={userForm.role} onValueChange={(value) => setUserForm({ ...userForm, role: value })}>
                      <SelectTrigger id="user-role" data-testid="user-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="reviewer">Reviewer</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="user-team">Team (Optional)</Label>
                    <Select value={userForm.team_id || 'none'} onValueChange={(value) => setUserForm({ ...userForm, team_id: value === 'none' ? '' : value })}>
                      <SelectTrigger id="user-team" data-testid="user-team-select">
                        <SelectValue placeholder="Select team" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Team</SelectItem>
                        {teams.map((team) => (
                          <SelectItem key={team.id} value={team.id}>
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-user-button">
                    Add User
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={isTeamDialogOpen} onOpenChange={setIsTeamDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#4338CA] hover:bg-[#3730A3] text-white" data-testid="create-team-button">
                  <Plus size={20} className="mr-2" />
                  Create Team
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Team</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateTeam} className="space-y-4" data-testid="team-form">
                  <div>
                    <Label htmlFor="team-name">Team Name</Label>
                    <Input
                      id="team-name"
                      placeholder="e.g., Customer Support Team"
                      value={teamForm.name}
                      onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                      required
                      data-testid="team-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="team-description">Description</Label>
                    <Textarea
                      id="team-description"
                      placeholder="Brief description of the team"
                      value={teamForm.description}
                      onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })}
                      data-testid="team-description-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-team-button">
                    Create Team
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Teams Grid */}
        {loading ? (
          <p className="text-slate-600">Loading teams...</p>
        ) : teams.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teams.map((team) => {
              const teamUsers = getUsersInTeam(team.id);
              return (
                <Card
                  key={team.id}
                  className="p-6 border border-slate-200 shadow-sm card-hover"
                  data-testid={`team-card-${team.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-indigo-50 rounded-lg">
                      <Users size={24} className="text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-900 mb-1">{team.name}</h3>
                      <p className="text-sm text-slate-600 mb-3">{team.description || 'No description'}</p>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                          {teamUsers.length} members
                        </span>
                      </div>
                      {teamUsers.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-slate-700">Members:</p>
                          {teamUsers.slice(0, 3).map((member) => (
                            <p key={member.id} className="text-xs text-slate-600">
                              {member.name} ({member.role})
                            </p>
                          ))}
                          {teamUsers.length > 3 && (
                            <p className="text-xs text-slate-500">+{teamUsers.length - 3} more</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="p-12 text-center border border-slate-200 shadow-sm">
            <Users size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 mb-4">No teams created yet</p>
            <Button onClick={() => setIsTeamDialogOpen(true)} data-testid="create-first-team-button">
              Create First Team
            </Button>
          </Card>
        )}

        {/* Unassigned Users */}
        {getUnassignedUsers().length > 0 && (
          <Card className="p-6 border border-amber-200 bg-amber-50 shadow-sm">
            <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Unassigned Users ({getUnassignedUsers().length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {getUnassignedUsers().map((user) => (
                <div
                  key={user.id}
                  className="p-3 bg-white border border-slate-200 rounded-lg"
                  data-testid={`unassigned-user-${user.id}`}
                >
                  <p className="font-medium text-slate-900">{user.name}</p>
                  <p className="text-sm text-slate-600">{user.email}</p>
                  <p className="text-xs text-slate-500 mt-1">{user.role}</p>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default TeamManagement;